import { useMsal } from "@azure/msal-react";
import { DirectionalHint, IColumn, IStackProps, Stack, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import InformationCallout from "components/controls/InformationCallout";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import saveAs from "file-saver";
import { appStyles, createNonStandard, createNonStandardTemplate, createNonStandardTemplateColumns, emitCustomEvent, fadeIn, friendlyValue } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, addUpdateNonStandardTemplates } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, formatDate, parseDate, timestamp } from "global/Utility/TSFunctions";
import {
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IObjectWithNameId as FAM_IObjectWithNameId,
    IStopLoss as FAM_IStopLoss,
    ITemplateRelationship as FAM_ITemplateRelationship,
    TGuid as FAM_TGuid
} from "model/FlexibleAttributeModel";
import { getEntityContext, createEntityUpdatePayload, IEntityContext } from 'global/Utility/EntityHelpers';
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import { EControlType } from "../../components/ControlWrapper";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { setProcessing } from "../../global/Reducer/statusSlice";
import { EVariant } from "./NonStandardEditor";
import NonStandardTemplateEditor from "./NonStandardTemplateEditor";
import { ESaveType, newNonStandardFromTemplateAction, SaveAsync, TemplateToBenefitOrGCAction } from "global/Application/dataActions";
import { $cat_$group_configuration_id } from "model/ConfigCategoryConstants";
import { $att_$non_standard_$bcbs_benefitdefinition_id, $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id, $att_$non_standard_$bcbs_effectivedate_id, $att_$non_standard_template_$bcbs_nst_benefitdefinition_id, $att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id, $att_$non_standard_template_$bcbs_nst_effectivedate_id } from "model/ConfigAttributeConstants";

interface IConfirm {
}


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    isNewNSAndChooseTemplate: boolean;
    sectionForNS?: IXrmTypes_bcbs_config_section;

    benefit?: FAM_IBenefit;
    groupConfiguration?: FAM_IGroupConfigurationData;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    stopLoss?: FAM_IStopLoss;

    onDismiss: () => void
}

enum ENonstandardScope {
    GroupConfiguration,
    Benefit,
    AccountsReceivable,
    FeesAndPremiums,
    StopLoss
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM_IObjectWithNameId {
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
}


interface INonstandardTemplateSearchResult extends FAM_IObjectWithNameId {
    item: FAM_INonStandardTemplate;
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IGroupConfigurationData;
    type: ENonstandardScope;
}

interface INonstandardTemplateScope {
    nonStandardTemplate: FAM_INonStandardTemplate;
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM_IBenefit
    type: ENonstandardScope.Benefit;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateSearchResult?: INonstandardTemplateScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardApplyTemplate = (props: IProps) => {
    // current view state/stages
    //const [nonStandardTemplate, setNonStandardTemplate] = useState<FAM.INonStandardTemplate>(props.nonStandardTemplate);

    const [linkedNSCategoryType, setLinkedNSCategoryType] = useState<IXrmTypes_bcbs_config_category | null>(null);
    const [entityContext, setEntityContext] = useState<IEntityContext | null>(null);

    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: props.state.data.snapshot,
        json: props.state.data.json,
    });

    const state = props.state;
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.isOpen) {
            setSourceContext({
                snapshot: props.state.data.snapshot,
                json: props.state.data.json,
                scopeSearchResult: null,
            });
            // Use EntityHelpers to get entity context
            const context = getEntityContext({
                data: props.state.data,
                benefit: props.benefit,
                groupConfiguration: props.groupConfiguration,
                accountsReceivable: props.accountsReceivable,
                feesAndPremium: props.feesAndPremium,
                stopLoss: props.stopLoss,
                sectionForNSConfigId: props.sectionForNS?._bcbs_config_categoryid_value
            });
            setEntityContext(context);

            //get the category - keep original logic but also use entity context
            let groupConfig: FAM_IGroupConfigurationData | undefined = undefined;
            let chosenBenefit: FAM_IBenefit | undefined = undefined;
            let chosenAccountsReceivable: FAM_IAccountsReceivable | undefined = undefined;
            let chosenFeesAndPremium: FAM_IFeesAndPremiums | undefined = undefined;
            let chosenStopLoss: FAM_IStopLoss | undefined = undefined;

            if (props.groupConfiguration) {
                groupConfig = props.groupConfiguration;
            } else if (props.benefit) {
                chosenBenefit = props.benefit;
            } else if (props.accountsReceivable) {
                chosenAccountsReceivable = props.accountsReceivable;
            } else if (props.feesAndPremium) {
                chosenFeesAndPremium = props.feesAndPremium;
            } else if (props.stopLoss) {
                chosenStopLoss = props.stopLoss;
            }

            // Use entity context if available, otherwise fall back to original logic
            if (context?.categoryConfigId) {
                let configCategory = props.state.data.config.cache.map_bcbs_config_category[context.categoryConfigId];
                setLinkedNSCategoryType(configCategory);
            } else {
                // Original fallback logic
                if (groupConfig) {
                    let configCategory = props.state.data.config.cache.map_bcbs_config_category[$cat_$group_configuration_id]; //group config category ID
                    setLinkedNSCategoryType(configCategory);
                } else if (chosenBenefit) {
                    let configCategory = props.state.data.config.cache.map_bcbs_config_category[chosenBenefit.bcbs_config_categoryid!];
                    setLinkedNSCategoryType(configCategory);
                } else if (chosenStopLoss) {
                    let configCategory = props.state.data.config.cache.map_bcbs_config_category_named['Stop Loss'];
                    setLinkedNSCategoryType(configCategory);
                }
            }
            
        }
    }, []);

    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardTemplateSearchResult>) => {

        if (!auditData?.data?.item)
            return <></>;

        const nonStandardTemplate = auditData?.data?.item;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardTemplateEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    disabled={true}
                    defaultValue={nonStandardTemplate ?? null}
                    openViewer={() => { }}
                />

            </div>
        </InformationCallout>;
    }

    /**
     * Search for all records applicable to selected scope
     * @param value 
     * @returns 
     */
    const onSearchNonStandardTemplates: TSearchPredicateAsync<INonstandardTemplateSearchResult> = async (value) => {
        const searchValue = value?.toLowerCase() ?? '';

        return new Promise((resolve, reject) => {

            const items: IGridItem<INonstandardTemplateSearchResult>[] = [];

            props.state.data.json.nonStandardTemplates?.forEach((nonStandardTemplateItem, ix) => {

                let add = false;

                if (!add && !searchValue)
                    add = true;

                if (!add && nonStandardTemplateItem.name.toLowerCase().indexOf(searchValue) > -1)
                    add = true;

                if (nonStandardTemplateItem.deactivated === true) {
                    add = false
                }

                let relationshipExists = false;

                const entityRelationship = nonStandardTemplateItem?.relationships?.find(rel => rel.categoryID === entityContext?.entityId);

                if (props.sectionForNS) {
                    // Create From Template path: show all templates of matching category,
                    // hide only if this entity already has an NS in this section from this template
                    if (entityRelationship) {
                        const sectionRef = entityRelationship.sectionRefs?.find(s => s.sectionId === props.sectionForNS!.bcbs_config_sectionid);
                        if (sectionRef && (sectionRef.nonStandards?.length ?? 0) > 0) {
                            relationshipExists = true;
                        }
                    }
                } else {
                    // Apply Template path (entity menu): hide if already linked to this entity
                    if (entityRelationship) {
                        relationshipExists = true;
                    }
                }
                //console.log("relationship exists: ", relationshipExists)
                let currentBenefitCategory = props.state.data.config.cache.map_bcbs_config_category[entityContext!.categoryConfigId!];
                
                if ((add && nonStandardTemplateItem?.linkedCategoryName === currentBenefitCategory.bcbs_name) && !relationshipExists && nonStandardTemplateItem.deactivated === false) {

                    const item: IGridItem<INonstandardTemplateSearchResult> = {
                        key: ix,
                        _index: ix,
                        name: nonStandardTemplateItem.name,
                        scope: "",
                        data: {
                            name: nonStandardTemplateItem.name,
                            id: nonStandardTemplateItem.id,
                            item: nonStandardTemplateItem,
                        }
                    };


                    nonStandardTemplateItem.fields.forEach(eaField => {
                        const configAttributeId = eaField.configRef.id;
                        eaField.values.forEach(eachFieldValue => {
                            item[`${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`] = eachFieldValue.value;
                        });
                    });

                    items.push(item);

                }
            })

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardTemplateSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardTemplateColumns(
                props.state.data.config,
                {
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    config: props.state.data.config,
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === $att_$non_standard_$bcbs_benefitdefinition_id)
                        return true;

                    return false;
                }
            );


            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }

    function onRenderBody(): JSX.Element {
        if (!entityContext) return <></>;
        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null}
                    label={'Non-Standard Templates'}
                    variant="multiple"
                    onSearch={onSearchNonStandardTemplates}
                    onPick={(picks) => {
                        const nonStandardTemplates: FAM_INonStandardTemplate[] = [];
                        //add template to this benefit
                        //adds non standards from template to current benefit/gc user is on
                        if(props.isNewNSAndChooseTemplate && props.sectionForNS)
                        {
                            //newNS from template - use EntityHelpers to create payload but keep original action
                            const chosenTemplates = picks?.map(q => q.item);
                            if (chosenTemplates){
                                const entityPayload = entityContext ? 
                                    createEntityUpdatePayload(entityContext.type, entityContext.entity) :
                                    { benefit: props.benefit, groupConfiguration: props.groupConfiguration, accountsReceivable: props.accountsReceivable, feesAndPremium: props.feesAndPremium };
                                
                                dispatch(newNonStandardFromTemplateAction(chosenTemplates, entityContext ?? null,
                                {section: props.sectionForNS, 
                                ...entityPayload}))
                            }

                        }else{
                            //apply template to benefit or GC - use EntityHelpers to create payload but keep original action
                            const chosenTemplates = picks?.map(q => q.item);
                            if (chosenTemplates){
                                const entityPayload = entityContext ? 
                                    createEntityUpdatePayload(entityContext.type, entityContext.entity) :
                                    { benefit: props.benefit, groupConfiguration: props.groupConfiguration, accountsReceivable: props.accountsReceivable, feesAndPremium: props.feesAndPremium };
                                
                                dispatch(TemplateToBenefitOrGCAction(chosenTemplates, entityContext))
                            }
                        }

                        
                        
                        setAlertIsOpen(true);
                        //props.onDismiss?.();
                    }}
                    onCancel={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                />

            </Stack>
        </div>
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                setAlertIsOpen(false); setAlertIsMounted(false);props.onDismiss?.();
                setTimeout(()=>{
                    SaveAsync(
                        dispatch,
                        dataSource,
                        props.state,
                        {saveType: ESaveType.EAuto});
                },100)
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Close"
            //cancelText="Close"
        >
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
        </ConfirmationDialog>;
    }

    function matchingNonStandard(incomingNonStandard: FAM_INonStandard, incomingTemplate: FAM_INonStandardTemplate, categoryToCheck: FAM_ITemplateRelationship) {
        let returnIds: FAM_TGuid[] = [];
        const templatedNonStandard = structuredClone(incomingNonStandard);
        const template = structuredClone(incomingTemplate);
        const templateFieldMap = ArrayToLookupByPredicate(template.fields, q => q.configRef.id);
        let incomingNSCategoryId = categoryToCheck.categoryID;
        
        const nonStandardFields = ArrayToLookupByPredicateWithMap(templatedNonStandard!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const nonStandardDefinition = nonStandardFields[$att_$non_standard_$bcbs_benefitdefinition_id];
        let nonStandardDefinitionValue = "";

        if (nonStandardDefinition.field.values[0].value !== null) {
            nonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, nonStandardDefinition.field.values[0].value);
        }

        let formattedNonStandardDefinition = nonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        //take incoming category id

        //only find section refs of section ref on ns
        categoryToCheck.sectionRefs?.forEach(section => {
            if (section.sectionId === templatedNonStandard.sectionRef?.id) {
                let category: FAM_IBenefit | FAM_IGroupConfigurationData;
                //check group config id
                if (props.state.data.json.id === incomingNSCategoryId) {
                    category = props.state.data.json;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap[$att_$non_standard_$bcbs_benefitdefinition_id];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
                //check benefitID's
                let benefit = props.state.data.json.benefits.find(benefit => benefit.id === incomingNSCategoryId);
                if (benefit) {
                    category = benefit;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap[$att_$non_standard_$bcbs_benefitdefinition_id];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
            }
        })

        return returnIds;
    }

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM_INonStandardTemplate) {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateEffectiveDate = templateFields[$att_$non_standard_template_$bcbs_nst_effectivedate_id];
        const templateDefinition = templateFields[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
        const templatePayAccordingToBenefit = templateFields[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
        let templateEffectiveDateValue = "";
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";

        if (templateEffectiveDate.field.values[0].value !== null) {
            templateEffectiveDateValue = formatDate(parseDate(friendlyValue(templateEffectiveDate.controlType, templateEffectiveDate.field.values[0].value), 'local:yyyy-MM-dd'), 'en-US-ET');
        }
        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef => {
                sectionRef.nonStandards?.forEach(nonStandardRef => {
                    //find the benefits for the category id
                    let benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                    if (benefit) {
                        //do a find on the non standard
                        let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);

                        //if we find the non standard, update the fields
                        if (benefitNonStandard && benefitNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM_INonStandard = structuredClone(benefitNonStandard);
                            updatedNonStandard.partOfTemplate = true;

                            let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_effectivedate_id);
                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                            const effectiveDateValue = templateEffectiveDate.field.values[0].value; //template value
                            nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value
                            //const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                            //nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                //groupConfiguration: props.groupConfiguration,
                                changeTracker: {}
                            }]));
                        }
                    } else if (props.state.data.json.id === categoryId) {
                        //if no benefits look at the GC
                        let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard && gcNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM_INonStandard = structuredClone(gcNonStandard);
                            updatedNonStandard.partOfTemplate = true;

                            let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_effectivedate_id);
                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                            const effectiveDateValue = templateEffectiveDate.field.values[0].value; //template value
                            nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value
                            //const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                            //nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                //groupConfiguration: props.groupConfiguration,
                                changeTracker: {}
                            }]));
                        }
                    }
                })
            })
        })
    }

    return onRenderBody()

}




function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardApplyTemplate);



